import { Linkedin, Loader2 } from 'lucide-react';
import React from 'react';

/**
 * CandidateTableRow Component
 * Table row for displaying candidate information
 *
 * @param {Object} props
 * @param {Object} props.candidate - Candidate data
 * @param {boolean} props.isSelected - Whether row is selected
 * @param {Function} props.onSelect - Selection handler
 * @param {Function} props.onActionsClick - Actions button handler
 */
export function CandidateTableRow({
  candidate,
  isSelected = false,
  onSelect,
  onActionsClick
}) {
  const isProcessing = candidate.status === 'processing';

  // Get LinkedIn URL (check both possible field names)
  const linkedinUrl = candidate.linkedin_url || candidate.linkedinUrl || candidate.contact_info?.linkedin;

  const photoUrl = candidate.photo_url || candidate.photoUrl || linkedinUrl
    ? `https://avatar.vercel.sh/${encodeURIComponent(candidate.name || 'user')}`
    : null;

  // Get top 3 skills
  const topSkills = candidate.skills?.slice(0, 3) || [];
  const remainingSkills = candidate.skills?.length > 3 ? candidate.skills.length - 3 : 0;

  // Format location (handle object or string)
  const formatLocation = (location) => {
    if (!location) return 'Location not specified';
    if (typeof location === 'string') return location;
    if (typeof location === 'object') {
      const { city, country } = location;
      if (city && country) return `${city}, ${country}`;
      if (city) return city;
      if (country) return country;
    }
    return 'Location not specified';
  };

  return (
    <tr className="hover:bg-gray-50 transition-colors">
      {/* Checkbox */}
      <td className="px-6 py-4">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={(e) => onSelect && onSelect(e.target.checked)}
          disabled={isProcessing}
          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50"
        />
      </td>

      {/* Candidate Info */}
      <td className="px-6 py-4">
        <div className="flex items-center">
          {photoUrl && (
            <img
              src={photoUrl}
              alt={candidate.name}
              className="w-12 h-12 rounded-full object-cover"
            />
          )}
          <div className={photoUrl ? 'ml-4' : ''}>
            <div className="text-sm font-medium text-gray-900">
              {candidate.name || 'Unknown'}
            </div>
            <div className="text-sm text-gray-500">
              {formatLocation(candidate.location)}
            </div>
            {linkedinUrl && (
              <a
                href={linkedinUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 mt-1"
              >
                <Linkedin className="w-3 h-3" />
                LinkedIn
              </a>
            )}
          </div>
        </div>
      </td>

      {/* Title & Company */}
      <td className="px-6 py-4">
        <div className="text-sm font-medium text-gray-900">
          {candidate.title || candidate.headline || 'Title not available'}
        </div>
        <div className="text-sm text-gray-500">
          {candidate.organization_name || candidate.company || 'Company not specified'}
        </div>
      </td>

      {/* Skills */}
      <td className="px-6 py-4">
        {isProcessing ? (
          <div className="flex items-center space-x-2">
            <Loader2 className="h-4 w-4 text-purple-600 animate-spin" />
            <span className="text-sm text-gray-500">Processing...</span>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {topSkills.map((skill, idx) => (
              <span key={idx} className="px-3 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                {skill}
              </span>
            ))}
            {remainingSkills > 0 && (
              <span className="px-3 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
                +{remainingSkills} more
              </span>
            )}
          </div>
        )}
      </td>

      {/* Match % */}
      <td className="px-6 py-4 text-center">
        <span className="text-sm font-semibold text-gray-500">
          {candidate.match_percentage || 0}%
        </span>
      </td>

      {/* Status */}
      <td className="px-6 py-4 text-center">
        {isProcessing ? (
          <span className="px-3 py-1 bg-purple-100 text-purple-800 text-xs font-medium rounded-full inline-flex items-center gap-1">
            <Loader2 className="h-3 w-3 animate-spin" />
            Processing
          </span>
        ) : (
          <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
            Processed
          </span>
        )}
      </td>

      {/* Actions */}
      <td className="px-6 py-4 text-center">
        <button
          className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed"
          onClick={() => onActionsClick && onActionsClick(candidate)}
          disabled={isProcessing}
        >
          {isProcessing ? 'Processing...' : 'Actions'}
        </button>
      </td>
    </tr>
  );
}



